import { useState } from "react";
import { Link } from "react-router-dom";
import Container from "@/components/base/Container";
import PageHeader from "@/components/feature/PageHeader";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Reveal from "@/components/feature/Reveal";
import { faqs, siteBrand } from "@/mocks/site";

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number>(0);

  const origin = typeof window !== "undefined" ? window.location.origin : "https://shenguashe.example.com";

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: `${origin}${__BASE_PATH__}` },
      { "@type": "ListItem", position: 2, name: "常见问题", item: `${origin}${__BASE_PATH__}faq` },
    ],
  };

  return (
    <>
      <Seo
        title="常见问题｜深瓜社的内容来源与时间线方法 - 深瓜社"
        description="深瓜社常见问题解答：内容来源如何核对、未删减原稿指什么、完整时间线怎么整理、是否会传播未经证实的传闻，以及投稿与转载规则。"
        keywords="大瓜深扒,完整时间线,未删减版,深度解析,内容来源,投稿规则"
        path="/faq"
        jsonLd={[faqSchema, breadcrumbSchema]}
      />
      <SiteHeader />
      <main>
        <PageHeader
          crumbs={[{ label: "常见问题" }]}
          eyebrow="常见问题"
          title="先把方法讲清楚，再开始吃瓜"
          description="这一页回答读者最常问的六个问题。如果你关心的是「你们凭什么这么说」，那答案基本都在这里。"
        />

        <Container className="py-10 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="flex flex-col gap-3">
                {faqs.map((item, index) => {
                  const isOpen = openIndex === index;
                  return (
                    <Reveal key={item.question} delay={index * 60}>
                      <article className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                        <h2>
                          <button
                            type="button"
                            onClick={() => setOpenIndex(isOpen ? -1 : index)}
                            aria-expanded={isOpen}
                            className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-5 text-left md:px-6"
                          >
                            <span className="flex items-start gap-3">
                              <span className="mt-0.5 font-heading text-sm font-bold text-primary-500">
                                {String(index + 1).padStart(2, "0")}
                              </span>
                              <span className="font-heading text-base font-semibold text-foreground-950 md:text-lg">
                                {item.question}
                              </span>
                            </span>
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-background-300 text-foreground-600">
                              <i className={isOpen ? "ri-subtract-line" : "ri-add-line"}></i>
                            </span>
                          </button>
                        </h2>
                        {isOpen ? (
                          <p className="border-t border-background-100 px-5 py-5 text-sm leading-[1.9] text-foreground-700 md:px-6 md:pl-14">
                            {item.answer}
                          </p>
                        ) : null}
                      </article>
                    </Reveal>
                  );
                })}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="flex flex-col gap-6 lg:sticky lg:top-24">
                <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-secondary-900">还没解决的问题</h2>
                  <p className="mt-2 text-sm leading-relaxed text-secondary-800">
                    如果你关心的是某一篇稿子的具体来源标注，欢迎直接来信，我们会把核查记录一并附上。
                  </p>
                  <a
                    href={`mailto:${siteBrand.email}`}
                    rel="nofollow"
                    className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-secondary-600"
                  >
                    <i className="ri-mail-send-line"></i>
                    联系编辑部
                  </a>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950">相关页面</h2>
                  <ul className="mt-4 space-y-3 text-sm">
                    <li>
                      <Link to="/about" className="flex items-center justify-between text-foreground-700 transition-colors hover:text-primary-700">
                        关于我们
                        <i className="ri-arrow-right-s-line"></i>
                      </Link>
                    </li>
                    <li>
                      <Link to="/timeline" className="flex items-center justify-between text-foreground-700 transition-colors hover:text-primary-700">
                        全网时间线
                        <i className="ri-arrow-right-s-line"></i>
                      </Link>
                    </li>
                    <li>
                      <Link to="/topics" className="flex items-center justify-between text-foreground-700 transition-colors hover:text-primary-700">
                        瓜田分类
                        <i className="ri-arrow-right-s-line"></i>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </Container>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}